/**
 * Fold a hook's effects after a handler's own lines — the one construction
 * every "event, then its effects" seam shares.
 *
 * @remarks
 * D143 wrote it for the wear pair's `onEquip` / `onUnequip`; D152's
 * `onSpent` is the third caller, which is the mandate to extract it
 * (engineering_standards §2.1). The fold is the `use` interaction's own: each
 * effect reads the state the previous one produced, and whatever it says
 * lands after what was already said.
 *
 * @packageDocumentation
 */

import type { EffectDefinition } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandInHand } from '../conditions/command-in-hand.js';

import { applyEffects } from '../effects/apply-effect.js';
import { type CompletedAction } from './action-handler-result.js';

/**
 * Apply a hook's effects to a completed action, in order.
 *
 * @param bundle - The loaded bundle
 * @param handled - The spoken event
 * @param effects - The hook's effects
 * @param commandInHand - D246: the sentence the handler answers, where a condition may read it (an authored verb's arm)
 * @returns The action with the effects applied
 */
export const withEffects = (
	bundle: EngineGameBundle,
	handled: CompletedAction,
	effects: readonly EffectDefinition[],
	commandInHand?: CommandInHand,
): CompletedAction => {
	const applied = applyEffects(effects, bundle, handled.state, handled.cursor, undefined, commandInHand);

	return { ...handled, cursor: applied.cursor, entries: [...handled.entries, ...applied.entries], state: applied.state };
};
